import React, { useEffect, useState } from 'react'
import SafeIcon from '../../../common/SafeIcon'
import { supabase, supabaseConfigError } from '../../../config/supabase'
import LessonFormDialog from './LessonFormDialog'
import * as FiIcons from 'react-icons/fi'

const { FiBook, FiPlus, FiEdit2, FiTrash2, FiArrowLeft, FiFilm, FiFileText, FiAlertTriangle } = FiIcons

const LessonManager = ({ courseId, curriculumId, totalDays = 180, courseName = '', onClose }) => {
  const [lessons, setLessons] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingLesson, setEditingLesson] = useState(null)

  const effectiveTotalDays = Math.max(1, Number(totalDays) || 180)

  useEffect(() => {
    if (courseId) {
      fetchLessons()
    } else {
      setLessons([])
      setLoading(false)
    }
  }, [courseId])

  const fetchLessons = async () => {
    if (!courseId || supabaseConfigError) {
      setLoading(false)
      if (supabaseConfigError) setError(supabaseConfigError.message)
      return
    }
    try {
      setLoading(true)
      setError(null)
      const { data, error: fetchError } = await supabase
        .from('lessons')
        .select('*')
        .eq('course_id', courseId)
        .order('day_number', { ascending: true })
        .order('order_index', { ascending: true })

      if (fetchError) throw fetchError
      setLessons(data || [])
    } catch (err) {
      console.error('Error fetching lessons', err)
      setError(err.message || 'Unable to load lessons.')
      setLessons([])
    } finally {
      setLoading(false)
    }
  }

  const handleAddLesson = () => {
    setEditingLesson(null)
    setIsFormOpen(true)
  }

  const handleEditLesson = (lesson) => {
    setEditingLesson(lesson)
    setIsFormOpen(true)
  }

  const handleFormClose = () => {
    setIsFormOpen(false)
    setEditingLesson(null)
  }

  const handleDelete = async (lessonId) => {
    const confirmed = window.confirm('Delete this lesson? This cannot be undone.')
    if (!confirmed) return

    if (supabaseConfigError) {
      setError(supabaseConfigError.message)
      return
    }
    try {
      const { error: deleteError } = await supabase.from('lessons').delete().eq('id', lessonId)
      if (deleteError) throw deleteError
      setLessons((prev) => prev.filter((l) => l.id !== lessonId))
    } catch (err) {
      console.error('Error deleting lesson', err)
      setError(err.message || 'Unable to delete lesson.')
    }
  }

  const lessonsByDay = lessons.reduce((acc, lesson) => {
    const day = Number(lesson.day_number) || 1
    if (!acc[day]) acc[day] = []
    acc[day].push(lesson)
    return acc
  }, {})
  const sortedDays = Object.keys(lessonsByDay)
    .map(Number)
    .sort((a, b) => a - b)

  const hasOutOfRangeDays = lessons.some(
    (l) => (Number(l.day_number) || 0) < 1 || (Number(l.day_number) || 0) > effectiveTotalDays
  )

  return (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-200 text-gray-700"
            aria-label="Back to courses"
          >
            <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
          </button>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {courseName || 'Lessons'}
            </h2>
            <p className="text-sm text-gray-500">Manage lessons for this course</p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleAddLesson}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#00AAFF] text-white hover:bg-[#0099E5]"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4" />
          Add Lesson
        </button>
      </div>

      <div className="p-5">
        {error && (
          <div className="flex items-start gap-2 p-3 mb-4 bg-red-50 border border-red-100 rounded-lg text-red-700">
            <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold">Error</p>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        )}

        {hasOutOfRangeDays && (
          <div className="flex items-start gap-2 p-3 mb-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
            <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <p>
              Some lessons have a day number outside 1–{effectiveTotalDays}. Edit them to fix, or increase curriculum total days.
            </p>
          </div>
        )}

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-20 rounded-xl bg-gray-100 animate-pulse" />
            ))}
          </div>
        ) : lessons.length === 0 ? (
          <div className="py-12 text-center border-2 border-dashed border-gray-200 rounded-xl">
            <SafeIcon icon={FiBook} className="w-14 h-14 mx-auto text-gray-300 mb-3" />
            <p className="font-semibold text-gray-700 mb-1">No lessons yet</p>
            <p className="text-sm text-gray-500 mb-4">Add your first lesson to this course.</p>
            <button
              type="button"
              onClick={handleAddLesson}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#00AAFF] text-white hover:bg-[#0099E5]"
            >
              <SafeIcon icon={FiPlus} className="w-4 h-4" />
              Create First Lesson
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {sortedDays.map((day) => (
              <div key={day}>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Day {day}
                </h3>
                <div className="space-y-2">
                  {lessonsByDay[day].map((lesson) => (
                    <div
                      key={lesson.id}
                      className="flex items-center gap-3 p-4 rounded-xl border border-gray-200 bg-white hover:border-[#00AAFF]/50 transition-colors"
                    >
                      <div className="flex items-center gap-2 text-gray-400">
                        {(lesson.video_url && lesson.content) && (
                          <>
                            <SafeIcon icon={FiFilm} className="w-4 h-4" />
                            <SafeIcon icon={FiFileText} className="w-4 h-4" />
                          </>
                        )}
                        {lesson.video_url && !lesson.content && <SafeIcon icon={FiFilm} className="w-4 h-4" />}
                        {!lesson.video_url && lesson.content && <SafeIcon icon={FiFileText} className="w-4 h-4" />}
                        {!lesson.video_url && !lesson.content && <SafeIcon icon={FiBook} className="w-4 h-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{lesson.title}</p>
                        <p className="text-sm text-gray-500">{lesson.subject}</p>
                      </div>
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-50 text-blue-700">
                        Day {lesson.day_number}
                      </span>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => handleEditLesson(lesson)}
                          className="p-2 rounded-lg hover:bg-gray-100 text-gray-700"
                          aria-label="Edit lesson"
                        >
                          <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(lesson.id)}
                          className="p-2 rounded-lg hover:bg-red-50 text-red-600"
                          aria-label="Delete lesson"
                        >
                          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <LessonFormDialog
        isOpen={isFormOpen}
        onClose={handleFormClose}
        courseId={courseId}
        curriculumId={curriculumId}
        totalDays={effectiveTotalDays}
        lesson={editingLesson}
        onSave={fetchLessons}
      />
    </div>
  )
}

export default LessonManager
